import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Search, Checkbox, Button, DismissibleTag } from "@carbon/react";
import { Add, Close, SendAlt } from "@carbon/icons-react";
import { ErrorPill, Loader } from "../..";
import {
  useInvitedMaterialSuppliers,
  useInviteOrderSupplier,
  useInviteSupplier,
  useSuppliersByBuyer,
  useUploadSupplier,
} from "../../../hooks/useSuppliers";
import { useNotification } from "../../../context";
import { useOrderInvitation } from "../../../context/hooks/useOrderInvitation";
import { UserResponse } from "../../../types";
import useDebounce from "../../../hooks/useDebounce";
import NewSupplierForm from "../../Form/NewSupplierForm";
import "./inviteSupplier.scss";

type InviteSupplierProps = {
  isJoint?: boolean;
  isOrder?: boolean;
  materialId?: string;
  materialName?: string;
  existingOrderIds?: string[];
  onCloseInvitation: () => void;
  orderInvitation?: any[];
  handleOrdersToSubmit?: (invite: any, type?: string) => void;
};

/**
 * The invite-suppliers form — rebuilt on Carbon (`Search`, `Checkbox`, `DismissibleTag`,
 * `Button`) replacing the pre-Carbon Tailwind inputs/chips/checkboxes. Every hook, handler and the
 * supplier-search + email-chip + new-supplier-create behaviour is unchanged; only the markup moved.
 * Rendered as content inside its callers' own container (a Carbon `ComposedModal` on the material
 * detail; the order/bid flows keep their own wrappers).
 */
const InviteSupplier = ({
  isOrder,
  isJoint,
  materialId,
  materialName,
  existingOrderIds,
  orderInvitation,
  onCloseInvitation,
  handleOrdersToSubmit,
}: InviteSupplierProps) => {
  const { openNotification } = useNotification();
  const { addUserToInvites } = useOrderInvitation();
  const isExistingOrder = !!(existingOrderIds && existingOrderIds.length > 0);

  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [emails, setEmails] = useState<string[]>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<UserResponse[]>(
    []
  );
  const [availableSuppliers, setAvailableSuppliers] = useState<UserResponse[]>(
    []
  );
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [successOrderInvite, setSuccessOrderInvite] = useState(false);
  const [hasError, setHasError] = useState<any>(null);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showNewSupplierForm, setShowNewSupplierForm] = useState(false);

  const { debouncedSearchTerm, isSearching: isDebouncingSearch } = useDebounce(
    value,
    500
  );

  const { data: invitedMaterialSuppliers } = useInvitedMaterialSuppliers(
    materialId || ""
  );

  const {
    data: newInvitation,
    mutate: mutateInviteSupplier,
    isPending,
  } = useInviteSupplier(
    (res) => {
      if (res?.find((el: any) => el.error)) {
        setHasError(res?.find((el: any) => el.error));
      }
      setEmails([]);
      setSelectedSuppliers([]);
      setAvailableSuppliers([]);
      return openNotification(t("invite.success"));
    },
    () => openNotification(t("newMaterialForm.error"))
  );

  const { data: suppliersByBuyer, isPending: isSearchinSuppliers } =
    useSuppliersByBuyer(0, debouncedSearchTerm, {
      enabled: debouncedSearchTerm.length >= 2,
    });

  const { mutate: createSupplier, isPending: isCreatingSupplier } =
    useUploadSupplier();

  const [, setOrderInviteCompleted] = useState(0);
  const [orderInviteTotal, setOrderInviteTotal] = useState(0);
  // N3: the backend returns a per-email result ({ error, email }); collect any
  // failures so we don't report "invitation sent" when nothing was sent.
  const inviteErrorsRef = useRef<string[]>([]);

  const { mutate: mutateInviteOrderSupplier, isPending: isInvitingOrder } =
    useInviteOrderSupplier(
      (res: any) => {
        if (Array.isArray(res)) {
          res.forEach((r: any) => {
            if (r?.error && r?.email) inviteErrorsRef.current.push(r.email);
          });
        }
        setOrderInviteCompleted((prev) => {
          const next = prev + 1;
          if (next >= orderInviteTotal) {
            setEmails([]);
            setSelectedSuppliers([]);
            setAvailableSuppliers([]);
            if (inviteErrorsRef.current.length > 0) {
              openNotification(
                t("invite.partialError", {
                  defaultValue:
                    "Some invitations could not be sent: {{emails}}",
                  emails: inviteErrorsRef.current.join(", "),
                })
              );
            } else {
              openNotification(t("invite.success"));
            }
            inviteErrorsRef.current = [];
            onCloseInvitation();
            setOrderInviteCompleted(0);
            setOrderInviteTotal(0);
          }
          return next;
        });
      },
      () => openNotification(t("newMaterialForm.error"))
    );

  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm.length >= 2) {
      setShowUserDropdown(true);
    } else {
      setShowUserDropdown(false);
    }
  }, [debouncedSearchTerm, suppliersByBuyer]);

  useEffect(() => {
    if (materialId) {
      const existingEmails =
        orderInvitation
          ?.find((item) => item.materialId === materialId)
          ?.emailsList?.split(":") || [];
      if (existingEmails.length > 0 && existingEmails[0] !== "") {
        setEmails(existingEmails);
      }
    }
  }, [materialId, orderInvitation]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    setError(null);
    setHasError(null);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (["Enter", "Tab", ","].includes(e.key)) {
      e.preventDefault();

      const email = value.trim().toLowerCase();

      if (email && isValid(email)) {
        setEmails((prevState) => [...prevState, email]);
        setValue("");
      }
    }
  };

  const handleDelete = (toBeRemoved: string) => {
    setEmails((prevState) =>
      prevState.filter((email) => email !== toBeRemoved)
    );
    setError(null);
    setHasError(null);
  };

  const handleToggleSupplier = (supplier: UserResponse) => {
    const isSelected = selectedSuppliers.find((s) => s.id === supplier.id);
    if (isSelected) {
      setSelectedSuppliers((prevState) =>
        prevState.filter((s) => s.id !== supplier.id)
      );
    } else {
      setSelectedSuppliers((prevState) => [...prevState, supplier]);
    }
  };

  const removeSupplier = (supplierId: string) => {
    setAvailableSuppliers((prevState) =>
      prevState.filter((s) => s.id !== supplierId)
    );
    setSelectedSuppliers((prevState) =>
      prevState.filter((s) => s.id !== supplierId)
    );
  };

  const isValid = (email: string) => {
    let error = null;
    if (isInList(email)) {
      error = `${email} ${t("invite.already")}`;
    }
    if (!isEmail(email)) {
      error = `${email} ${t("invite.notValid")}`;
    }
    if (error) {
      setError(error);
      return false;
    }
    return true;
  };

  const isInList = (email: string) => {
    return (
      emails.includes(email) ||
      selectedSuppliers.some((supplier) => supplier.email === email)
    );
  };

  const isEmail = (email: string) => {
    return /[\w\d.-]+@[\w\d.-]+\.[\w\d.-]+/.test(email);
  };

  const handleOrderInvitation = (
    existingSuppliers: UserResponse[],
    inviteEmails: string[]
  ) => {
    setIsLoading(true);

    if (existingSuppliers.length > 0) {
      existingSuppliers.forEach((supplier) => {
        const supplierData = {
          materialId: isJoint && !materialId ? "ALL" : materialId,
          supplierId: supplier.id,
          supplierInfo: {
            id: supplier.id,
            email: supplier.email,
            name:
              `${supplier.firstName || ""} ${supplier.lastName || ""}`.trim() ||
              (supplier as any).userFullName ||
              "",
            companyName:
              supplier.company?.name || (supplier as any).companyName || "",
          },
        };

        if (isOrder && isJoint && handleOrdersToSubmit) {
          handleOrdersToSubmit(supplierData, "existingSupplier");
        } else if (isOrder && handleOrdersToSubmit) {
          handleOrdersToSubmit(supplierData, "existingSupplier");
        }
      });
    }

    if (inviteEmails.length > 0) {
      const effectiveMaterialId =
        isJoint && !materialId ? "ALL" : materialId || "";

      addUserToInvites({
        materialId: effectiveMaterialId,
        emailsList: inviteEmails.join(":"),
      });

      if (isOrder && handleOrdersToSubmit) {
        handleOrdersToSubmit(
          {
            materialId: effectiveMaterialId,
            emails: inviteEmails,
          } as unknown as { emailsList: string; materialId: string },
          "setEmailInvites"
        );
      }
    }

    setTimeout(() => {
      setIsLoading(false);
      setSuccessOrderInvite(true);
    }, 1000);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (isExistingOrder) {
      const allEmails = [
        ...emails,
        ...selectedSuppliers.map((s) => s.email).filter(Boolean),
      ];
      if (allEmails.length === 0 || !existingOrderIds) return;

      const emailsList = allEmails.join(":");
      setOrderInviteCompleted(0);
      setOrderInviteTotal(existingOrderIds.length);

      existingOrderIds.forEach((orderId) => {
        mutateInviteOrderSupplier({ emails: emailsList, orderId });
      });
      return;
    }

    if (isOrder) {
      handleOrderInvitation(selectedSuppliers, emails);
    } else {
      const emailsList = [
        ...emails,
        ...selectedSuppliers.map((s) => s.email),
      ].join(":");
      mutateInviteSupplier({
        emails: emailsList,
        materialId: materialId || "",
      });
    }
  };

  const handleSupplierSelect = (user: UserResponse | Record<string, any>) => {
    const u = user as Record<string, any>;
    const normalizedSupplier: UserResponse = {
      ...user,
      id: u.id,
      email: u.email,
      company: u.company || { name: u.companyName || "" },
      firstName: u.firstName ?? u.userFullName?.split(" ")[0] ?? "",
      lastName:
        u.lastName ?? u.userFullName?.split(" ").slice(1).join(" ") ?? "",
    } as UserResponse;

    if (
      !availableSuppliers.find(
        (supplier) => supplier.id === normalizedSupplier.id
      )
    ) {
      setAvailableSuppliers((prevState) => [...prevState, normalizedSupplier]);
    }
    if (
      !selectedSuppliers.find(
        (supplier) => supplier.id === normalizedSupplier.id
      )
    ) {
      setSelectedSuppliers((prevState) => [...prevState, normalizedSupplier]);
    }
    setValue("");
    setShowUserDropdown(false);
    setError(null);
  };

  const handleCreateSupplier = (supplierData: any) => {
    createSupplier(supplierData, {
      onSuccess: (newSupplier) => {
        setAvailableSuppliers((prevState) => [
          ...prevState,
          {
            ...newSupplier[0],
            company: { name: newSupplier[0].companyName },
          },
        ]);
        setSelectedSuppliers((prevState) => [
          ...prevState,
          {
            ...newSupplier[0],
            company: { name: newSupplier[0].companyName },
          },
        ]);
        setShowNewSupplierForm(false);
        openNotification(t("suppliers.success"));
        setValue("");
      },
      onError: () => {
        openNotification(t("newMaterialForm.error"));
      },
    });
  };

  const supplierLabel = (supplier: UserResponse) =>
    supplier.company?.name ||
    (supplier as any).companyName ||
    `${supplier.firstName || ""} ${supplier.lastName || ""}`.trim() ||
    (supplier as any).userFullName ||
    "";

  if (isPending || isLoading || isCreatingSupplier || isInvitingOrder)
    return <Loader isFullScreen />;

  if (isOrder && successOrderInvite) {
    openNotification(t("invite.success"));
    onCloseInvitation();
    setSuccessOrderInvite(false);
  }

  const searchResults = Array.isArray(suppliersByBuyer)
    ? suppliersByBuyer
    : suppliersByBuyer?.content || [];
  const canSubmit = emails.length > 0 || selectedSuppliers.length > 0;

  return (
    <div className="invite-supplier">
      {hasError ? <ErrorPill list={newInvitation.data} /> : null}
      {materialName && (
        <h3 className="invite-supplier__material">{materialName}</h3>
      )}

      <form onSubmit={handleSubmit} className="invite-supplier__form">
        {availableSuppliers.length > 0 && (
          <div className="invite-supplier__selected">
            {availableSuppliers.map((supplier) => (
              <div key={supplier.id} className="invite-supplier__selected-row">
                <Checkbox
                  id={`supplier-${supplier.id}`}
                  labelText={`${supplierLabel(supplier)} (${supplier.email})`}
                  checked={
                    !!selectedSuppliers.find((s) => s.id === supplier.id)
                  }
                  onChange={() => handleToggleSupplier(supplier)}
                />
                <Button
                  kind="ghost"
                  size="sm"
                  hasIconOnly
                  renderIcon={Close}
                  iconDescription={t("cta.remove")}
                  tooltipPosition="left"
                  onClick={() => removeSupplier(supplier.id)}
                />
              </div>
            ))}
          </div>
        )}

        {emails.length > 0 && (
          <div className="invite-supplier__chips">
            {emails.map((email) => (
              <DismissibleTag
                key={email}
                type="blue"
                size="md"
                text={email}
                onClose={() => handleDelete(email)}
                title={t("cta.remove")}
              />
            ))}
          </div>
        )}

        <div className="invite-supplier__search">
          <Search
            id="invite-supplier-search"
            size="lg"
            labelText={t("invite.message")}
            placeholder={t("invite.searchPlaceholder", {
              defaultValue: "Buscar por nombre, email o empresa...",
            })}
            value={value}
            onChange={(e) =>
              handleChange(e as React.ChangeEvent<HTMLInputElement>)
            }
            onKeyDown={onKeyDown}
            onBlur={() => {
              setTimeout(() => setShowUserDropdown(false), 200);
            }}
            onFocus={() => {
              if (searchResults.length > 0 && value.length >= 2) {
                setShowUserDropdown(true);
              }
            }}
          />

          {value.length >= 2 &&
            (isSearchinSuppliers || isDebouncingSearch) && (
              <div className="invite-supplier__dropdown invite-supplier__dropdown--loading">
                <Loader />
              </div>
            )}

          {showUserDropdown && value.length >= 2 && !isSearchinSuppliers && (
            <div className="invite-supplier__dropdown">
              {searchResults.length > 0 ? (
                <>
                  {searchResults.map((user: any) => (
                    <button
                      key={user.id}
                      type="button"
                      className="invite-supplier__option"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleSupplierSelect(user);
                      }}
                    >
                      <span className="invite-supplier__option-name">
                        {user.companyName ||
                          user.company?.name ||
                          `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                          user.userFullName}
                      </span>
                      <span className="invite-supplier__option-email">
                        {user.email}
                      </span>
                    </button>
                  ))}
                  <button
                    type="button"
                    className="invite-supplier__option invite-supplier__option--add"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setShowUserDropdown(false);
                      setShowNewSupplierForm(true);
                    }}
                  >
                    <Add size={16} />
                    {t("suppliers.add")}
                  </button>
                </>
              ) : (
                <div className="invite-supplier__no-results">
                  <p>
                    {t(
                      "suppliers.noSuppliersFound",
                      "No se encontraron proveedores"
                    )}
                  </p>
                  <Button
                    kind="tertiary"
                    size="sm"
                    renderIcon={Add}
                    onMouseDown={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setShowUserDropdown(false);
                      setShowNewSupplierForm(true);
                    }}
                  >
                    {t("suppliers.add")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {error ? <p className="invite-supplier__error">{error}</p> : null}

        <Button
          type="submit"
          renderIcon={SendAlt}
          disabled={!canSubmit}
          className="invite-supplier__submit"
        >
          {t("invite.send")}
        </Button>
      </form>

      {showNewSupplierForm && (
        <div className="invite-supplier__new">
          <NewSupplierForm
            onSupplierCreated={handleCreateSupplier}
            showAsModal={true}
            onCancel={() => setShowNewSupplierForm(false)}
          />
        </div>
      )}

      {!isOrder &&
        invitedMaterialSuppliers &&
        invitedMaterialSuppliers?.filter((sup) => sup.used === false)?.length >
          0 && (
          <div className="invite-supplier__pending">
            <p className="invite-supplier__pending-title">
              {t("invite.pending")}
            </p>
            <ul className="invite-supplier__pending-list">
              {invitedMaterialSuppliers
                ?.filter((sup) => sup.used === false)
                .map((sup) => (
                  <li key={sup.id}>{sup.email}</li>
                ))}
            </ul>
          </div>
        )}
    </div>
  );
};

export default InviteSupplier;
